/** Tipos do domínio do pipeline e mapeamento do contrato da API (DEC-030). */

/** Resultado paginado padrão do backend (PagedResult<T>). */
export interface PagedResult<T> {
  items: T[];
  total: number;
  skip: number;
  take: number;
}

/**
 * DealListItem como vem da API (camelCase). `eTag` é o RowVersion em base64 puro. Lemos o ETag de
 * forma tolerante (eTag/etag) por causa de como o System.Text.Json trata o acrônimo no camelCase.
 */
export interface ApiDealListItem {
  id: string;
  code: string;
  codename: string;
  currentStage: string;
  auxiliaryStatus: string;
  legalStatus: string;
  stageEnteredAtUtc: string;
  initialEntryAtUtc: string;
  leadUserId: string;
  leadUserName: string | null;
  investmentValueBRL: number | null;
  /** Parte discricionária do cheque (Item 3/4) — alimenta o KPI "Pipeline value discricionário". */
  checkDiscretionaryBRL: number | null;
  /** Parte de coinvestimento — pré-preenche o split no handoff. */
  checkCoinvestmentBRL: number | null;
  isPriority: boolean;
  targetCompanyId: string | null;
  companyName: string | null;
  thesisNames: string[];
  teamUserNames: string[];
  eTag?: string;
  etag?: string;
}

/** DealResponse (GET/PUT/move). Usado no toggle de prioritário (GET + PUT) e no retorno do move. */
export interface ApiDealResponse {
  id: string;
  code: string;
  codename: string;
  legalStatus: string;
  folderId: string | null;
  targetCompanyId: string | null;
  originatorIdSnapshot: string | null;
  originatorPersonIdSnapshot: string | null;
  internalOriginatorId: string | null;
  leadUserId: string;
  currentStage: string;
  stageEnteredAtUtc: string;
  auxiliaryStatus: string;
  initialEntryAtUtc: string;
  investmentValueBRL: number | null;
  /** Split do cheque (Item 3): total = discricionário + coinvestimento. */
  checkDiscretionaryBRL: number | null;
  checkCoinvestmentBRL: number | null;
  thesisSummary: string | null;
  isPriority: boolean;
  thesisIds: number[];
  teamUserIds: string[];
  tags: string[];
  eTag?: string;
  etag?: string;
}

/** Deal no domínio do frontend (normalizado a partir do ApiDealListItem). */
export interface Deal {
  id: string;
  code: string;
  codename: string;
  /** apiName da fase (ex.: "AnaliseIC1"). */
  stage: string;
  auxiliaryStatus: string;
  legalStatus: string;
  stageEnteredAtUtc: string;
  /** Data da 1ª entrada no funil. Usada como base de "dias nesta fase" só quando stage = Originacao. */
  initialEntryAtUtc: string;
  leadUserId: string;
  leadUserName: string;
  investmentValueBRL: number | null;
  /** Parte discricionária do cheque (Item 3/4) — base do KPI discricionário da toolbar. */
  checkDiscretionaryBRL: number | null;
  /** Parte de coinvestimento do cheque — pré-preenche o split no handoff. */
  checkCoinvestmentBRL: number | null;
  isPriority: boolean;
  companyId: string | null;
  companyName: string;
  thesisNames: string[];
  teamUserNames: string[];
  etag: string;
}

/** Lê o ETag de uma resposta da API tolerando a variação de casing do acrônimo. */
export function readApiETag(raw: { eTag?: string; etag?: string }): string {
  return raw.eTag ?? raw.etag ?? '';
}

/** Normaliza um item da listagem para o domínio (fallbacks de nome e ETag). */
export function toDeal(raw: ApiDealListItem): Deal {
  return {
    id: raw.id,
    code: raw.code,
    codename: raw.codename,
    stage: raw.currentStage,
    auxiliaryStatus: raw.auxiliaryStatus,
    legalStatus: raw.legalStatus,
    stageEnteredAtUtc: raw.stageEnteredAtUtc,
    // Fallback defensivo: backend antigo (pré-PR InitialEntryAtUtc no list) → usa a entrada de fase.
    initialEntryAtUtc: raw.initialEntryAtUtc ?? raw.stageEnteredAtUtc,
    leadUserId: raw.leadUserId,
    leadUserName: raw.leadUserName ?? '—',
    investmentValueBRL: raw.investmentValueBRL,
    checkDiscretionaryBRL: raw.checkDiscretionaryBRL ?? null,
    checkCoinvestmentBRL: raw.checkCoinvestmentBRL ?? null,
    isPriority: raw.isPriority,
    companyId: raw.targetCompanyId,
    // Sem empresa-alvo (item 1) → fallback claro; com empresa mas nome ausente → fallback antigo.
    companyName: raw.targetCompanyId == null ? 'Sem empresa' : (raw.companyName ?? '(empresa sem nome)'),
    thesisNames: raw.thesisNames ?? [],
    teamUserNames: raw.teamUserNames ?? [],
    etag: readApiETag(raw),
  };
}
